import { IntegrationHealth } from 'types';

/**
 * Severity ranking used to sort the overview-page health table so problematic
 * integrations float to the top and healthy ones sink to the bottom.
 *
 * Order MUST match the decision order in
 * `renderIntegrationHealthByRequestAndSuccessCountPercent` (`utils/display.tsx`):
 * the renderer checks auth → rate-limit → success-rate buckets, so the
 * sort key must check rate-limit before the success-rate buckets too.
 * Otherwise a row showing the rate-limit icon could sort into the "critical"
 * bucket and confuse the viewer.
 *
 *  0  Auth Failed  (LockIcon, red)        — auth errors present + < 95% success
 *  1  Rate Limited (TimerOffIcon, orange) — InvalidParamsError > 50% of errors
 *  2  Critical     (ErrorIcon, red)       — success rate < 50% (no rate-limit)
 *  3  Degraded     (WarningIcon, orange)  — 50-79% success
 *  4  Good         (InfoIcon, blue)       — 80-94% success
 *  5  Healthy      (CheckCircleIcon, green) — >= 95% success
 *  6  Inactive     (PauseCircleIcon, gray) — configured but no requests today
 *  7  Not Configured (LinkOffIcon, gray)  — no health record at all
 */
export const HEALTH_SEVERITY = {
  AUTH_FAILED: 0,
  RATE_LIMITED: 1,
  CRITICAL: 2,
  DEGRADED: 3,
  GOOD: 4,
  HEALTHY: 5,
  INACTIVE: 6,
  NOT_CONFIGURED: 7,
} as const;

export type HealthSeverity = (typeof HEALTH_SEVERITY)[keyof typeof HEALTH_SEVERITY];

const isEmptyObj = (obj: unknown): boolean =>
  !obj || typeof obj !== 'object' || Object.keys(obj as Record<string, unknown>).length === 0;

export const getHealthSeverityRank = (health?: IntegrationHealth): HealthSeverity => {
  if (!health || isEmptyObj(health)) return HEALTH_SEVERITY.NOT_CONFIGURED;

  const { requestCount = 0, successCount = 0, errors } = health;
  if (requestCount === 0) return HEALTH_SEVERITY.INACTIVE;

  const successRate = (successCount / requestCount) * 100;
  const errorEntries = errors ? Object.entries(errors) : [];
  const totalErrors = errorEntries.reduce((sum, [, count]) => sum + (count || 0), 0);
  const hasAuthError = !!errors?.AuthorizationError && errors.AuthorizationError > 0;
  const rateLimitDominates =
    !!errors?.InvalidParamsError &&
    totalErrors > 0 &&
    errors.InvalidParamsError > totalErrors * 0.5;

  // Match the renderer's decision order: auth → rate-limit → success-rate
  // buckets. Each branch corresponds to exactly one icon state.
  if (hasAuthError && successRate < 95) return HEALTH_SEVERITY.AUTH_FAILED;
  if (rateLimitDominates) return HEALTH_SEVERITY.RATE_LIMITED;
  if (successRate < 50) return HEALTH_SEVERITY.CRITICAL;
  if (successRate < 80) return HEALTH_SEVERITY.DEGRADED;
  if (successRate < 95) return HEALTH_SEVERITY.GOOD;
  return HEALTH_SEVERITY.HEALTHY;
};
